import { useMemo, useState, useCallback } from 'react';
import type { User, Placement, Currency, Owner } from '../lib/types';
import { todayIso } from '../lib/format';
import { runForecast } from '../lib/forecast';
import { isForecastRelevant } from '../lib/runners';
import { selectionForOids, type Selection } from '../lib/splits';
import { useFx } from '../hooks/useFx';
import { BookValueHeader } from './BookValueHeader';
import { RenewalSlider } from './RenewalSlider';
import { ForecastChart } from './ForecastChart';
import { ContractorTable } from './ContractorTable';
import { TeamBreakdown, type BreakdownPerson } from './TeamBreakdown';
import { TeamFilter, type TeamFilterValue } from './TeamFilter';
import { ContractorDetail } from './ContractorDetail';

interface Props {
  user: User;
  placements: Placement[];
}

export function Forecaster({ user, placements }: Props) {
  const today = todayIso();
  const [viewDate, setViewDateState] = useState<string>(today);
  const [renewalPct, setRenewalPct] = useState(0);
  const [filterOid, setFilterOid] = useState<TeamFilterValue>('all');
  const [selected, setSelected] = useState<Placement | null>(null);

  // Display currency — placements arrive in the viewer's own currency
  // (USD for Chicago Contract, GBP otherwise), which is also the default
  // here; the toggle re-denominates for display using the /api/fx rates.
  const [displayCurrency, setDisplayCurrency] = useState<Currency>(user.currency);
  const fx = useFx();
  const fxFactor = useMemo(() => {
    if (displayCurrency === user.currency) return 1;
    const from = fx?.unitsPerGbp[user.currency];
    const to = fx?.unitsPerGbp[displayCurrency];
    if (!from || !to) return 1;
    return to / from;
  }, [fx, displayCurrency, user.currency]);
  const canToggleCurrency = Boolean(fx?.unitsPerGbp.GBP && fx?.unitsPerGbp.USD);
  const effectiveCurrency: Currency = fxFactor === 1 && displayCurrency !== user.currency
    ? user.currency // rates unavailable — fall back to the viewer's currency
    : displayCurrency;

  const displayPlacements = useMemo(() => {
    if (fxFactor === 1) return placements;
    return placements.map((p) => ({
      ...p,
      wgp: p.wgp * fxFactor,
      wgpCurrency: effectiveCurrency,
    }));
  }, [placements, fxFactor, effectiveCurrency]);

  // Whenever the view date moves, reset the slider to 0% so the user starts
  // from "raw forecast" and can dial up renewals deliberately.
  const setViewDate = useCallback((newDate: string) => {
    setViewDateState(newDate);
    setRenewalPct(0);
  }, []);

  // Changing the team filter re-defaults the display currency to match the
  // selection (Chicago → USD, London → GBP); the toggle can still override.
  const handleFilterChange = useCallback(
    (v: TeamFilterValue) => {
      setFilterOid(v);
      if (v === 'chicago' || v === 'nyc') {
        setDisplayCurrency('USD');
      } else if (v === 'london' || v === 'consult' || v === 'deploy') {
        setDisplayCurrency('GBP');
      } else if (v === 'all' || v === 'connect') {
        setDisplayCurrency(user.currency);
      } else if (
        (user.scope.chicago ?? []).some((o) => o.oid === v) ||
        (user.scope.nyc ?? []).some((o) => o.oid === v)
      ) {
        setDisplayCurrency('USD');
      } else if ((user.scope.london ?? []).some((o) => o.oid === v)) {
        setDisplayCurrency('GBP');
      } else if ((user.scope.currencyRouted ?? []).some((o) => o.oid === v)) {
        // "Other" (routed perm-desk) person — mixed-currency book.
        setDisplayCurrency(user.currency);
      }
    },
    [user],
  );

  // Who gets credited in the current view. Team-level selections also carry
  // the currency-routed perm-desk members: their shares count on GBP deals
  // for London views and USD deals for Chicago views.
  const selection = useMemo<Selection>(() => {
    if (user.role === 'self') return selectionForOids(new Set([user.scope.self!.oid]));

    const routed: ReadonlySet<string> = new Set(
      (user.scope.currencyRouted ?? []).map((o) => o.oid),
    );
    const londonOids = new Set((user.scope.london ?? []).map((o) => o.oid));
    const chicagoOids = new Set((user.scope.chicago ?? []).map((o) => o.oid));
    const nycOids = new Set((user.scope.nyc ?? []).map((o) => o.oid));
    // Connect = London + Chicago + NYC contractors.
    const connectOids = new Set<string>([...londonOids, ...chicagoOids, ...nycOids]);
    const routedCurrenciesInScope = () => {
      const currencies = new Set<string>();
      if (user.scope.london) currencies.add('GBP');
      if (user.scope.chicago) currencies.add('USD');
      return currencies;
    };

    if (filterOid === 'london') {
      return { direct: londonOids, routed, routedCurrencies: new Set(['GBP']) };
    }

    if (filterOid === 'chicago') {
      return { direct: chicagoOids, routed, routedCurrencies: new Set(['USD']) };
    }

    if (filterOid === 'nyc') {
      return selectionForOids(nycOids);
    }

    // Solution-desk House books — direct credit only, no currency routing.
    if (filterOid === 'consult') {
      return selectionForOids(new Set((user.scope.consult ?? []).map((o) => o.oid)));
    }
    if (filterOid === 'deploy') {
      return selectionForOids(new Set((user.scope.deploy ?? []).map((o) => o.oid)));
    }

    if (filterOid === 'connect') {
      return { direct: connectOids, routed, routedCurrencies: routedCurrenciesInScope() };
    }

    if (filterOid === 'all') {
      // "All Teams" / a director's whole team — Connect plus the solution
      // desks; routed currencies cover whichever cities are in scope.
      const direct = new Set<string>([
        ...connectOids,
        ...(user.scope.consult ?? []).map((o) => o.oid),
        ...(user.scope.deploy ?? []).map((o) => o.oid),
      ]);
      return { direct, routed, routedCurrencies: routedCurrenciesInScope() };
    }

    // Specific person selected — their own splits only, no routing.
    return selectionForOids(new Set([filterOid]));
  }, [user, filterOid]);

  // People who hold an owner role on at least one current or upcoming
  // placement — anyone else is hidden from the team filter lists.
  const activeOids = useMemo<ReadonlySet<string>>(() => {
    const s = new Set<string>();
    for (const p of placements) {
      if (!isForecastRelevant(p, today)) continue;
      for (const o of [p.owners.cro, p.owners.consultant, p.owners.ao, p.owners.conro]) {
        if (o) s.add(o.oid);
      }
    }
    return s;
  }, [placements, today]);

  // Per-person rows for the Team Breakdown — shown on team-level selections.
  const breakdownPeople = useMemo<BreakdownPerson[]>(() => {
    const people = new Map<string, BreakdownPerson>();
    const addDirect = (list?: Owner[]) => {
      for (const o of list ?? []) if (!people.has(o.oid)) people.set(o.oid, { owner: o, routed: false });
    };
    const addRouted = () => {
      for (const o of user.scope.currencyRouted ?? []) {
        if (!people.has(o.oid)) people.set(o.oid, { owner: o, routed: true });
      }
    };

    const isDirectorWholeTeam = user.role === 'director' && filterOid === 'all';
    if (isDirectorWholeTeam || filterOid === 'london') addDirect(user.scope.london);
    if (isDirectorWholeTeam || filterOid === 'chicago') addDirect(user.scope.chicago);
    if (filterOid === 'nyc' || filterOid === 'connect' || (user.role === 'leadership' && filterOid === 'all')) {
      if (filterOid !== 'nyc') {
        addDirect(user.scope.london);
        addDirect(user.scope.chicago);
      }
      addDirect(user.scope.nyc);
    }
    if (user.role === 'leadership' && filterOid === 'all') {
      addDirect(user.scope.consult);
      addDirect(user.scope.deploy);
    }
    if (people.size > 0 && filterOid !== 'nyc') addRouted();

    return people.size > 1 ? [...people.values()] : [];
  }, [user, filterOid]);

  const forecast = useMemo(
    () =>
      runForecast({
        placements: displayPlacements,
        selection,
        today,
        viewDate,
        renewalPct,
      }),
    [displayPlacements, selection, today, viewDate, renewalPct],
  );

  const viewerOid = user.scope.self?.oid ?? user.oid;

  return (
    <main className="ds-page">
      <BookValueHeader
        viewDate={viewDate}
        onViewDateChange={setViewDate}
        bookValue={forecast.bookValueAtViewDate}
        runnerCount={forecast.runnersAtViewDate}
        runnersBySplit={forecast.runnersBySplitAtViewDate}
        startingSoonCount={forecast.startingSoonCount}
        currency={effectiveCurrency}
        onCurrencyChange={canToggleCurrency ? setDisplayCurrency : undefined}
        todayBook={{ value: forecast.bookValueToday, runners: forecast.runnersToday }}
        toolbar={
          user.role === 'director' || user.role === 'leadership' ? (
            <TeamFilter user={user} selectedOid={filterOid} onChange={handleFilterChange} activeOids={activeOids} />
          ) : undefined
        }
      />

      <RenewalSlider
        renewalPct={renewalPct}
        onChange={setRenewalPct}
        atRiskCount={forecast.atRiskCount}
        poolCount={forecast.poolCount}
        renewCount={forecast.renewCount}
        dropCount={forecast.dropCount}
      />

      <ForecastChart
        forecast={forecast}
        currency={effectiveCurrency}
        viewDate={viewDate}
        today={today}
        onEventClick={setSelected}
      />

      {breakdownPeople.length > 0 && (
        <TeamBreakdown
          projected={forecast.projected}
          people={breakdownPeople}
          routedCurrencies={selection.routedCurrencies}
          currency={effectiveCurrency}
          viewDate={viewDate}
          onSelectPerson={handleFilterChange}
        />
      )}

      <ContractorTable
        projected={forecast.projected}
        viewerOid={viewerOid}
        selection={selection}
        currency={effectiveCurrency}
        viewDate={viewDate}
      />

      {selected && (
        <ContractorDetail
          placement={selected}
          viewerOid={viewerOid}
          selection={selection}
          currency={effectiveCurrency}
          onClose={() => setSelected(null)}
        />
      )}
    </main>
  );
}
